/** A2A Protocol types (v0.3.0) */

// Chat protocol types live in their own module so consumers can import them
// without pulling A2A's full surface.
export type {
  AckFrame,
  AuthErrorFrame,
  AuthFrame,
  AuthOkFrame,
  ChannelJoinFrame,
  ChatConfig,
  ChatDeliverFrame,
  ChatErrorFrame,
  ChatFrame,
  ChatIdentity,
  ChatMessage,
  ChatMessageInboundFrame,
  ChatReplyFrame,
  HistoryChunkFrame,
  HistoryRequestFrame,
  PingFrame,
  PongFrame,
  TypingFrame,
} from "./chat-types.js";

export interface AgentCard {
  name: string;
  description: string;
  protocolVersion: string;
  version: string;
  url: string;
  skills: AgentSkill[];
  capabilities: { pushNotifications: boolean };
  defaultInputModes: string[];
  defaultOutputModes: string[];
}

export interface AgentSkill {
  id: string;
  name: string;
  description: string;
  tags: string[];
}

export interface Part {
  kind: "text" | "file" | "data";
  text?: string;
  mimeType?: string;
  data?: unknown;
}

export interface Message {
  kind: "message";
  messageId: string;
  role: "user" | "agent";
  parts: Part[];
  contextId?: string;
  metadata?: Record<string, unknown>;
}

export interface TaskStatus {
  state: "submitted" | "working" | "completed" | "failed" | "canceled";
  timestamp: string;
  message?: Partial<Message>;
}

export interface Artifact {
  kind: "artifact";
  artifactId: string;
  parts: Part[];
}

export interface Task {
  kind: "task";
  id: string;
  contextId: string;
  status: TaskStatus;
  messages: Message[];
  artifacts: Artifact[];
  metadata?: Record<string, unknown>;
  sessionKeyHint?: string;
}

export interface JsonRpcRequest {
  jsonrpc: "2.0";
  method: string;
  params: Record<string, unknown>;
  id: string;
}

export interface JsonRpcResponse {
  jsonrpc: "2.0";
  id: string;
  result?: unknown;
  error?: { code: number; message: string };
}

export interface PeerConfig {
  [agentName: string]: string;
}

export interface RelayConfig {
  /** WebSocket URL of the relay hub (e.g. wss://relay.perkos.xyz) */
  url: string;
  /** API key for authenticating with the relay hub */
  apiKey: string;
  /** Whether relay connectivity is enabled */
  enabled: boolean;
  /** Port used when this process runs as a relay hub (mode: relay). */
  hubPort?: number;
  /** Accepted API keys when this process runs as a relay hub. */
  hubApiKeys?: string[];
  /** Explicit approved-agent registry when this process runs as a relay hub: agentName -> registration API key. */
  registeredAgents?: Record<string, string>;
}

export interface AuthConfig {
  /** Require API key for inbound HTTP requests */
  requireApiKey: boolean;
  /** Accepted API keys for inbound HTTP requests */
  apiKeys: string[];
}

export interface A2APluginConfig {
  agentName: string;
  port: number;
  /** Host/interface to bind the local HTTP server. Defaults to 0.0.0.0 for Docker compatibility. */
  bindHost?: string;
  /** Externally reachable base URL advertised in the Agent Card, e.g. tunnel/DNS/LAN URL. */
  publicUrl?: string;
  skills: AgentSkill[];
  peers: PeerConfig;
  mode?: "full" | "client-only" | "relay" | "auto";
  /**
   * Where inbound A2A tasks should be delivered for execution.
   *
   * OpenClaw is the default because this package is primarily an OpenClaw
   * plugin. Hermes Workspace can also be targeted through its local HTTP API,
   * which lets the same A2A relay/direct protocol bridge OpenClaw <-> Hermes.
   */
  runtime?: RuntimeDeliveryConfig;
  /** API keys for authenticating outbound requests to specific peers */
  peerAuth?: { [agentName: string]: string };
  relay?: RelayConfig;
  auth?: AuthConfig;
  /**
   * Optional PerkOS-Chat client. When `enabled`, the plugin opens a WS to
   * `chat.perkos.xyz` alongside the A2A relay so the agent can participate
   * in user-facing project chats and 1-on-1 DMs.
   *
   * Separate from the relay because chat traffic has different volume and
   * rate-limit characteristics than task dispatch. See `src/chat-client.ts`.
   */
  chat?: import("./chat-types.js").ChatConfig;
}

export type RuntimeKind = "openclaw" | "hermes" | "hermes-api" | "none";

export interface RuntimeDeliveryConfig {
  /** Delivery target for inbound tasks. Defaults to openclaw. */
  kind?: RuntimeKind;
  /** Session to wake/send into. OpenClaw default: agent:main. Hermes API default: a2a. */
  sessionKey?: string;
  /** Hermes API Server base URL, e.g. http://127.0.0.1:8642. */
  hermesUrl?: string;
  /** Optional Hermes API Server bearer token/API key. */
  hermesToken?: string;
  /** Optional endpoint override. Defaults to /v1/responses. */
  hermesEndpoint?: string;
}

/** Wire protocol for relay WebSocket messages */
export type RelayMessageType =
  | "register"
  | "register_ack"
  | "task"
  | "task_response"
  | "discover"
  | "discover_response"
  | "heartbeat"
  | "heartbeat_ack"
  | "webhook_event"
  | "error";

/**
 * Payload of a `webhook_event` frame. Transport delivers these when an
 * external system (GitHub, GitLab, Linear, generic) fires a webhook
 * that matches one of this agent's subscriptions. Hooks-first design:
 * the agent does not need to expose its own HTTP surface.
 *
 * `headers` contains only the small set of upstream headers that are
 * useful downstream (delivery id, user-agent). `body` is the parsed
 * JSON body, or `null` when the upstream payload wasn't JSON.
 */
export interface WebhookEventPayload {
  subscriptionId: string;
  source: "github" | "gitlab" | "linear" | "generic" | string;
  eventType: string;
  headers: {
    delivery?: string;
    userAgent?: string;
  };
  body: unknown;
}

/** Convenience helper: narrows a RelayMessage to a webhook_event frame. */
export function isWebhookEvent(
  msg: RelayMessage,
): msg is RelayMessage & { type: "webhook_event"; payload: WebhookEventPayload } {
  return msg.type === "webhook_event";
}

export interface RelayMessage {
  type: RelayMessageType;
  /** Sender agent name */
  from?: string;
  /** Target agent name (for routed messages) */
  to?: string;
  /** Unique message identifier */
  id: string;
  /** Message payload */
  payload: Record<string, unknown>;
  /** ISO timestamp */
  timestamp: string;
}

export interface RelayAgentEntry {
  name: string;
  connectedAt: string;
  lastHeartbeat: string;
  card?: AgentCard;
}
